import { formatDateWithMask, formatHeliosDateValue, isDateValid } from 'utils/dateAndTime';
import { formatHeliosNumber, formatNumberAsTime } from 'utils/helios/numberFormatting';
import { roundedToNumber } from 'utils/helios/rounding';
import { RoundedToValue } from 'types/dataHelios';
import type { HeliosCellData } from 'types/helios/agendaListing';

const isHeliosCellData = (value: unknown): value is HeliosCellData => typeof value === 'object' && value !== null && 'rawValue' in value;

const formatHeliosCellValue = (cellData: HeliosCellData, locale: string): string => {
  const { rawValue, formatting } = cellData;

  if (rawValue === null || rawValue === undefined) {
    return '';
  }

  const formatNumber = formatting?.formatValue?.typeSpecific?.formatNumber;
  if (formatNumber) {
    const parsedNumericValue = typeof rawValue === 'number' ? rawValue : typeof rawValue === 'string' ? Number(rawValue) : NaN;

    if (formatNumber.showAsTime && Number.isFinite(parsedNumericValue)) {
      return formatNumberAsTime(parsedNumericValue);
    }

    const rounding = roundedToNumber[formatNumber.roundTo ?? RoundedToValue.Hundreds];

    if (Number.isFinite(parsedNumericValue)) {
      const numericValue = formatHeliosNumber(parsedNumericValue, locale, {
        minimumFractionDigits: Math.max(0, rounding),
        maximumFractionDigits: Math.max(0, rounding),
        thousandsSeparator: formatNumber.thousandsSeparator,
        roundTo: formatNumber.roundTo,
        mathRound: formatNumber.mathRound,
      });

      const unit = formatNumber.unit ? ` ${formatNumber.unit}` : '';
      return `${numericValue}${unit}`;
    }

    return String(rawValue);
  }

  const formatDateTime = formatting?.formatValue?.typeSpecific?.formatDateTime;
  if (formatDateTime) {
    const dateStr = typeof rawValue === 'string' ? rawValue : String(rawValue);
    const variant = cellData.userDefinedAttributeType ?? formatDateTime.variant;
    const formattedDate = formatHeliosDateValue(dateStr, variant, 'dd.MM.yyyy HH:mm');

    if (formattedDate) {
      return formattedDate;
    }

    return dateStr;
  }

  return typeof rawValue === 'object' && rawValue !== null ? rawValue.name : String(rawValue);
};

/**
 * Formats a raw table cell value into a human-readable display string.
 *
 * Handles:
 * - Helios cell data objects (with rawValue + formatting)
 * - Flow Date arrays ([dateString, dateMask])
 * - Plain strings, numbers, nulls
 *
 * @param value - The raw cell value from table transformed data
 * @param isHelios - Whether the table uses Helios data format
 * @param locale - ISO locale string (e.g. 'cs-CZ', 'en-GB') for number formatting
 */
export const formatDisplayValue = (value: unknown, isHelios: boolean, locale: string): string => {
  if (isHelios && isHeliosCellData(value)) {
    return formatHeliosCellValue(value, locale);
  }

  if (Array.isArray(value) && value.length >= 2) {
    const dateStr = value[0];
    const dateMask = value[1];
    if (typeof dateStr === 'string' && typeof dateMask === 'string' && dateStr.length > 0) {
      return isDateValid(dateStr) ? formatDateWithMask(dateStr, dateMask) : dateStr;
    }
  }

  return String(value ?? '');
};
